import { useEffect, useState } from "react";

// Compact W × H input pair shown above the layers panel. The numbers are
// the user's intended export resolution (in pixels). The canvas editor
// fits a thin gray rectangle of that aspect ratio into the available
// space and crops to it on export.
//
// Local state mirrors the committed value so the user can type freely
// (including a transient empty input) without immediately resizing the
// frame on every keystroke. Commit on blur or Enter.

export default function CanvasSetup({ width, height, onChange }) {
  const [w, setW] = useState(String(width));
  const [h, setH] = useState(String(height));

  useEffect(() => { setW(String(width)); }, [width]);
  useEffect(() => { setH(String(height)); }, [height]);

  const commit = () => {
    const nw = parseInt(w, 10);
    const nh = parseInt(h, 10);
    if (!Number.isFinite(nw) || !Number.isFinite(nh) || nw <= 0 || nh <= 0) {
      setW(String(width));
      setH(String(height));
      return;
    }
    if (nw !== width || nh !== height) onChange(nw, nh);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") { e.currentTarget.blur(); }
    else if (e.key === "Escape") {
      setW(String(width)); setH(String(height));
      e.currentTarget.blur();
    }
  };

  return (
    <div className="mb-2 rounded-2xl border-2 border-ink/15 bg-paper-deep px-3 py-2">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-ink-soft">
        Canvas size
      </div>
      <div className="flex items-center gap-2">
        <NumberInput value={w} onChange={setW} onCommit={commit} onKeyDown={handleKey} label="W" />
        <span className="text-xs text-ink-soft">×</span>
        <NumberInput value={h} onChange={setH} onCommit={commit} onKeyDown={handleKey} label="H" />
        <span className="ml-auto text-[10px] tabular-nums text-ink-soft">
          {width}:{height} = {(width / height).toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function NumberInput({ value, onChange, onCommit, onKeyDown, label }) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-ink-soft">
      <span>{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={onKeyDown}
        className="w-20 rounded-lg border-2 border-ink/20 bg-white px-2 py-1 text-xs font-semibold text-ink outline-none focus:border-ink"
      />
    </label>
  );
}
